
import { Group, Product, UsageRecord } from "@/lib/types";
import GroupStatsCards from "./GroupStatsCards";
import UsageByProductChart from "./UsageByProductChart";
import UsageOverTimeChart from "./UsageOverTimeChart";
import CriticalProductAlert from "./CriticalProductAlert";
import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface GroupAnalyticsProps {
  selectedGroupId: string;
  groups: Group[];
  products: Product[];
  usageRecords: UsageRecord[];
  timeRange: "7days" | "30days" | "90days";
}

const GroupAnalytics = ({ 
  selectedGroupId, 
  groups, 
  products, 
  usageRecords,
  timeRange
}: GroupAnalyticsProps) => {
  // Don't show this component if "all-groups" is selected
  if (selectedGroupId === "all-groups") return null;

  const [usageTimeRange, setUsageTimeRange] = useState<"1month" | "3months" | "6months">("1month");

  // Find the selected group
  const selectedGroup = groups.find(g => g.id === selectedGroupId);
  if (!selectedGroup) return null;

  // Get products in the selected group
  const groupProducts = products.filter(p => p.groupIds.includes(selectedGroupId));
  if (groupProducts.length === 0) return null;
  
  return (
    <div className="grid grid-cols-1 gap-8 mb-8">
      <h2 className="text-xl font-semibold">
        Group Analytics: {selectedGroup.name}
      </h2>
      
      <div className="mb-4">
        <h3 className="text-sm font-medium mb-2">Average Daily Usage Period:</h3>
        <Tabs 
          value={usageTimeRange} 
          onValueChange={(value: "1month" | "3months" | "6months") => setUsageTimeRange(value)}
          className="w-full sm:w-[300px]"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="1month">1 Month</TabsTrigger>
            <TabsTrigger value="3months">3 Months</TabsTrigger>
            <TabsTrigger value="6months">6 Months</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      <GroupStatsCards
        groupProducts={groupProducts}
        usageRecords={usageRecords}
        usageTimeRange={usageTimeRange}
        timeRange={timeRange}
      />
      
      <UsageByProductChart
        groupProducts={groupProducts}
        usageRecords={usageRecords}
        timeRange={timeRange}
      />

      <UsageOverTimeChart
        groupProducts={groupProducts}
        usageRecords={usageRecords}
      />
      
      <CriticalProductAlert
        groupProducts={groupProducts}
        usageRecords={usageRecords}
        usageTimeRange={usageTimeRange}
      />
    </div>
  );
};

export default GroupAnalytics;
